import axios from 'axios';
import type { ApiError } from '../../common-showcase/types/showcase';
import type { ScaffoldRequest, ScaffoldPreviewResponse } from '../types/scaffold';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

/**
 * scaffoldApi — project-scaffold biz (queue #642 · docs/biz-specs/project-scaffold.md §5).
 * JSON 왕복(previews)은 axios apiClient(#630 showcaseApi 패턴 재사용: 인증 인터셉터 +
 * ApiResponse<T> 언랩 + WAF/프록시 변조 방어). zip 다운로드(projects)는 blob 응답이 axios
 * 오류 인터셉터를 무력화하므로 raw fetch 로 status 를 직접 분기(#631/#641 패턴 재사용).
 */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

function getStoredToken(): string | null {
  return localStorage.getItem('access_token') ?? sessionStorage.getItem('access_token');
}

apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      const fieldErrors = err?.['fieldErrors'] as unknown[] | undefined;
      return Promise.reject({
        code: (err?.['code'] as string) ?? `HTTP-${response.status}`,
        message: (err?.['message'] as string) ?? 'Unknown error',
        detail: (err?.['detail'] as string) ?? undefined,
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
        statusCode: response.status,
        fieldErrors: fieldErrors ?? [],
      } as ApiError);
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const rawData = error.response?.data;
    const statusCode: number = error.response?.status ?? 0;
    const respCt = String(error.response?.headers?.['content-type'] ?? '');

    // R11-#308 패턴 재사용: WAF/프록시가 4xx/5xx 를 HTML 로 변조하는 경우 명시적으로 식별.
    const looksLikeHtml = respCt.includes('text/html')
      || (typeof rawData === 'string' && rawData.trimStart().startsWith('<'));

    const code = looksLikeHtml
      ? 'NETWORK-WAF-001'
      : ((errData?.['code'] as string) ?? `HTTP-${statusCode || 'UNKNOWN'}`);
    const message = looksLikeHtml
      ? '응답이 JSON 이 아닙니다 — 토큰 만료 또는 WAF/프록시 변조 가능성. 페이지를 새로고침하고 재로그인 해주세요.'
      : ((errData?.['message'] as string)
          ?? (typeof rawData === 'object' && rawData !== null
                ? ((rawData as Record<string, unknown>)['message'] as string)
                : undefined)
          ?? (statusCode > 0 ? `HTTP ${statusCode} — ${error.message ?? 'Unknown error'}` : 'Network error'));
    const detail = (errData?.['detail'] as string) ?? undefined;
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    const fieldErrors = (errData?.['fieldErrors'] as unknown[]) ?? [];
    return Promise.reject({ code, message, detail, traceId, statusCode, fieldErrors } as ApiError);
  }
);

export interface ScaffoldZipResult {
  blob: Blob;
  filename: string;
}

export const scaffoldApi = {
  /** POST /api/eraf/scaffold/previews */
  previews: (req: ScaffoldRequest): Promise<ScaffoldPreviewResponse> =>
    apiClient
      .post<unknown, { data: ScaffoldPreviewResponse }>('/scaffold/previews', req)
      .then((res) => res.data),

  /**
   * POST /api/eraf/scaffold/projects — 골격 zip (queue #641 exportModuleSourceBundle 와 동형
   * raw fetch 다운로드 패턴 재사용 — blob 응답은 axios 오류 인터셉터를 무력화하므로 apiClient 우회).
   */
  projects: async (req: ScaffoldRequest): Promise<ScaffoldZipResult> => {
    const base = import.meta.env.VITE_API_BASE ?? '/api/eraf';
    const token = getStoredToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${base}/scaffold/projects`, {
      method: 'POST',
      headers,
      body: JSON.stringify(req),
    });

    if (!response.ok) {
      let errCode = `HTTP-${response.status}`;
      let message = `골격 생성에 실패했습니다 (HTTP ${response.status})`;
      try {
        const body = (await response.json()) as { error?: { code?: string; message?: string } };
        if (body?.error?.code) errCode = body.error.code;
        if (body?.error?.message) message = body.error.message;
      } catch {
        // 응답이 JSON 이 아닌 경우 — 기본 메시지 유지
      }
      return Promise.reject({
        code: errCode,
        message,
        statusCode: response.status,
      } as ApiError);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : `${req.projectName}-scaffold.zip`;
    const blob = await response.blob();

    return { blob, filename };
  },
};
